import { useRef, useState, useCallback } from "react";
import { FileIcon, Upload, Loader2, CheckCircle2, AlertCircle, X, Trash2 } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

export type FileStatus = "pending" | "uploading" | "processing" | "completed" | "error";

export interface FileWithStatus {
  file?: File;
  id?: number; // ID del documento en BD
  name: string;
  status: FileStatus;
  message?: string;
  url?: string;
}

interface DocumentDropzoneProps {
  files: FileWithStatus[];
  onFilesChange: (files: FileWithStatus[]) => void;
  onUpload: (files: File[]) => Promise<void>;
  onDelete?: (fileId: number) => Promise<void>;
  disabled?: boolean;
  accept?: string;
  maxFiles?: number;
  hideFilesList?: boolean;
}

const STATUS_COLORS = {
  pending: "#6b7280", // gray
  uploading: "#f59e0b", // yellow/amber
  processing: "#f59e0b", // yellow/amber
  completed: "#10b981", // green
  error: "#ef4444", // red
};

const STATUS_ICONS = {
  pending: FileIcon,
  uploading: Loader2,
  processing: Loader2,
  completed: CheckCircle2,
  error: AlertCircle,
};

function getStatusColor(status: FileStatus): string {
  return STATUS_COLORS[status];
}

function getStatusIcon(status: FileStatus) {
  return STATUS_ICONS[status];
}

export function DocumentDropzone({
  files,
  onFilesChange,
  onUpload,
  onDelete,
  disabled = false,
  accept = "application/pdf",
  maxFiles,
  hideFilesList = false,
}: DocumentDropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  const validateFiles = useCallback(
    (fileList: FileList | null): File[] => {
      if (!fileList || fileList.length === 0) return [];

      const selectedFiles = Array.from(fileList);

      // Validar que solo sean PDFs
      if (accept?.includes("application/pdf") || accept === "application/pdf") {
        const pdfFiles = selectedFiles.filter(
          (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
        );

        if (pdfFiles.length !== selectedFiles.length) {
          setError("Solo se permiten archivos PDF");
          return [];
        }
      }

      // Validar número máximo de archivos
      if (maxFiles && selectedFiles.length > maxFiles) {
        setError(`Solo se permiten máximo ${maxFiles} archivo(s)`);
        return [];
      }

      setError("");
      return selectedFiles;
    },
    [accept, maxFiles]
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      const validFiles = validateFiles(fileList);
      if (validFiles.length === 0) return;

      const newFiles: FileWithStatus[] = validFiles.map((file) => ({
        file,
        name: file.name,
        status: "pending" as FileStatus,
      }));

      onFilesChange([...files, ...newFiles]);
    },
    [files, onFilesChange, validateFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || isUploading) return;
      e.preventDefault();
      setDragOver(true);
    },
    [disabled, isUploading]
  );

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled || isUploading) return;
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [disabled, isUploading, handleFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input para permitir seleccionar el mismo archivo de nuevo
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [handleFiles]
  );

  const handleUpload = useCallback(async () => {
    const pendingFiles = files.filter((f) => f.status === "pending" && f.file);
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    setError("");

    // Actualizar estados a "uploading"
    const updatedFiles = files.map((f) =>
      pendingFiles.some((pf) => pf.file === f.file) ? { ...f, status: "uploading" as FileStatus } : f
    );
    onFilesChange(updatedFiles);

    try {
      const filesToUpload = pendingFiles.map((f) => f.file!).filter(Boolean);
      await onUpload(filesToUpload);

      // Actualizar estados a "processing" después de subir
      const processingFiles = files.map((f) =>
        pendingFiles.some((pf) => pf.file === f.file) ? { ...f, status: "processing" as FileStatus } : f
      );
      onFilesChange(processingFiles);
    } catch (err) {
      // Actualizar estados a "error" en caso de error
      const errorFiles = files.map((f) =>
        pendingFiles.some((pf) => pf.file === f.file)
          ? { ...f, status: "error" as FileStatus, message: "Error al subir archivo" }
          : f
      );
      onFilesChange(errorFiles);
      setError("Error al subir archivos. Por favor, intenta de nuevo.");
    } finally {
      setIsUploading(false);
    }
  }, [files, onUpload, onFilesChange]);

  const handleRemovePending = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
    },
    [files, onFilesChange]
  );

  const handleDelete = useCallback(
    async (fileId: number) => {
      if (!onDelete) return;
      try {
        await onDelete(fileId);
        const newFiles = files.filter((f) => f.id !== fileId);
        onFilesChange(newFiles);
        setError(""); // Limpiar errores previos
      } catch (err) {
        const errorMessage = err instanceof Error
          ? err.message
          : "Error desconocido al eliminar el archivo";
        setError(`Error al eliminar: ${errorMessage}`);
        // El error se mostrará en el toast del componente padre también
      }
    },
    [files, onDelete, onFilesChange]
  );

  const pendingFiles = files.filter((f) => f.status === "pending");
  const canUpload = pendingFiles.length > 0 && !isUploading && !disabled;
  const canAddFiles = !isUploading && !disabled;

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        role="button"
        tabIndex={canAddFiles ? 0 : -1}
        onClick={() => {
          if (canAddFiles) inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (canAddFiles && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative rounded-lg border-2 border-dashed transition-all",
          canAddFiles
            ? "cursor-pointer hover:border-primary/50 hover:bg-accent/50"
            : "cursor-not-allowed opacity-60",
          dragOver && canAddFiles && "border-primary bg-primary/5 scale-[1.02]"
        )}
        style={{
          borderColor: dragOver && canAddFiles ? getStatusColor("uploading") : undefined,
        }}
      >
        <div className="flex flex-col items-center justify-center p-8 text-center">
          {isUploading ? (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-lg font-semibold">Subiendo archivos...</p>
              <p className="text-sm text-muted-foreground mt-2">
                Por favor espera mientras se suben los archivos
              </p>
            </>
          ) : (
            <>
              <Upload className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold">
                {canAddFiles ? "Arrastra archivos PDF aquí o haz clic para seleccionar" : "Carga deshabilitada"}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Solo se permiten archivos PDF
              </p>
            </>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={handleFileInputChange}
          disabled={!canAddFiles}
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Files list */}
      {!hideFilesList && files.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Archivos</h3>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {files.map((fileItem, index) => {
              const StatusIcon = getStatusIcon(fileItem.status);
              const statusColor = getStatusColor(fileItem.status);
              const isSpinning = fileItem.status === "uploading" || fileItem.status === "processing";

              return (
                <div
                  key={fileItem.id || `pending-${index}`}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    {isSpinning ? (
                      <StatusIcon
                        className="h-5 w-5 animate-spin"
                        style={{ color: statusColor }}
                      />
                    ) : (
                      <StatusIcon className="h-5 w-5" style={{ color: statusColor }} />
                    )}
                  </div>

                  {/* File name and status */}
                  <div className="flex-1 min-w-0">
                    {fileItem.url ? (
                      <a
                        href={fileItem.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:underline break-words"
                      >
                        {fileItem.name}
                      </a>
                    ) : (
                      <p className="text-sm font-medium break-words">{fileItem.name}</p>
                    )}
                    {fileItem.message && (
                      <p className="text-xs text-muted-foreground mt-1">{fileItem.message}</p>
                    )}
                    {!fileItem.message && fileItem.status === "completed" && (
                      <p className="text-xs text-muted-foreground mt-1">Archivo cargado correctamente</p>
                    )}
                    {fileItem.status === "uploading" && (
                      <p className="text-xs text-muted-foreground mt-1">Subiendo...</p>
                    )}
                    {fileItem.status === "processing" && (
                      <p className="text-xs text-muted-foreground mt-1">Procesando...</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {fileItem.status === "pending" && (
                      <Button
                        variant="icon"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleRemovePending(index)}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    {fileItem.id && fileItem.status !== "pending" && onDelete && (
                      <Button
                        variant="icon"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(fileItem.id!)}
                        disabled={isUploading || fileItem.status === "uploading"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload button */}
      {pendingFiles.length > 0 && (
        <Button
          onClick={handleUpload}
          disabled={!canUpload}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Subiendo...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Subir {pendingFiles.length} archivo{pendingFiles.length > 1 ? "s" : ""}
            </>
          )}
        </Button>
      )}
    </div>
  );
}
